import { useSearchParams } from 'react-router-dom';
import * as yup from 'yup';
import { SEARCH_PARAMS_KEYS } from './searchParamsKeys';
import { captureException } from '@sentry/react';

export const searchParamsSchema = yup.object({
  [SEARCH_PARAMS_KEYS.EXPANDED]: yup.string().oneOf(['true']),
  [SEARCH_PARAMS_KEYS.FILTERS_ACTIVITY]: yup.string(),
  [SEARCH_PARAMS_KEYS.FILTERS_COMMODITIES]: yup.string(),
  [SEARCH_PARAMS_KEYS.FILTERS_COUNTRY]: yup.string(),
  [SEARCH_PARAMS_KEYS.FILTERS_PANEL]: yup.string().oneOf(['true']),
  [SEARCH_PARAMS_KEYS.FILTERS_PRECISION]: yup.string().oneOf(['true']),
  [SEARCH_PARAMS_KEYS.FILTERS_PRODUCT]: yup.string(),
  [SEARCH_PARAMS_KEYS.FILTERS_RISK]: yup
    .string()
    .oneOf(['low', 'high', 'processing']),
  [SEARCH_PARAMS_KEYS.FILTERS_STATUS]: yup.string(),
  [SEARCH_PARAMS_KEYS.FILTERS_SUPPLIER]: yup.string(),
  [SEARCH_PARAMS_KEYS.FILTERS_SURFACE_AREA]: yup.string(),
  [SEARCH_PARAMS_KEYS.FILTERS_UPDATED_AT]: yup.string(),
  [SEARCH_PARAMS_KEYS.IMPORT_MODAL]: yup.string().oneOf(['open']),
  [SEARCH_PARAMS_KEYS.PLOT_ID]: yup.string().uuid(),
  [SEARCH_PARAMS_KEYS.PLOTS]: yup.string().oneOf(['1', '0']),
  [SEARCH_PARAMS_KEYS.RISK_MODAL]: yup.string().oneOf(['open']),
  [SEARCH_PARAMS_KEYS.SEARCH]: yup.string(),
  [SEARCH_PARAMS_KEYS.SUPPLIER_ID]: yup.string().uuid(),
  [SEARCH_PARAMS_KEYS.USER_PANEL]: yup.string().oneOf(['true']),
  [SEARCH_PARAMS_KEYS.DDS]: yup.string(),
});

export type ValidatedSearchParams = yup.InferType<typeof searchParamsSchema>;
export type SearchParamKey = keyof ValidatedSearchParams;

const useValidatedSearchParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const paramsObject = Object.fromEntries(searchParams.entries());

  const setValidatedParam = (key: SearchParamKey, value: string | null) => {
    if (value === null) {
      searchParams.delete(key);
      setSearchParams(searchParams);
      return;
    }

    try {
      // Validate just the single parameter being set
      searchParamsSchema.validateSync({ [key]: value }, { stripUnknown: true });
      searchParams.set(key, value);
      setSearchParams(searchParams);
    } catch (error) {
      captureException(error);
      console.error(error);
    }
  };

  try {
    const validatedParams = searchParamsSchema.validateSync(paramsObject, {
      stripUnknown: true,
      abortEarly: false,
    });

    return {
      validatedParams,
      searchParams,
      setSearchParams,
      setValidatedParam,
      SEARCH_PARAMS_KEYS,
    };
  } catch (error) {
    captureException(error);
    console.error('Invalid search params', error);
    return {
      validatedParams: {} as ValidatedSearchParams,
      searchParams,
      setSearchParams,
      setValidatedParam,
      SEARCH_PARAMS_KEYS,
    };
  }
};

export default useValidatedSearchParams;
